'use client';

import { c as _c } from "react/compiler-runtime";
import { ChangeEvent, useEffect, useState } from 'react';
import { useMemo } from 'react';
import { MdArrowDownward } from 'react-icons/md';
import { useDebouncedTransitionValue } from '../hooks/useDebouncedTransitionValue';
import { useLazyScroll } from '../hooks/useLazyScroll';
import { cn } from '../lib/utils';
import { SearchInput } from './search-input';
import { INITIAL_VISIBLE_PROJECTS, PROJECTS_EMPTY_STATE_ID, PROJECTS_GRID_ID } from './projects-constants';
interface ProjectsControlsProps {
  searchIndex: string[];
}
const getProjectCards = (): HTMLElement[] => {
  const grid = document.getElementById(PROJECTS_GRID_ID);
  if (!grid) return [];
  return Array.from(grid.querySelectorAll<HTMLElement>('[data-project-card]'));
};
export default function ProjectsControls(t0) {
  const $ = _c(38);
  const {
    searchIndex
  } = t0;
  const [searchTerm, setSearchTerm] = useState("");
  const [visibleCount, setVisibleCount] = useState(INITIAL_VISIBLE_PROJECTS);
  const debouncedSearchTerm = useDebouncedTransitionValue(searchTerm, 300);
  let t1;
  if ($[0] !== debouncedSearchTerm || $[1] !== searchIndex) {
    const term = debouncedSearchTerm.trim().toLowerCase();
    t1 = searchIndex.reduce((matches, entry, index) => {
      if (entry.includes(term)) {
        matches.push(index);
      }
      return matches;
    }, []);
    $[0] = debouncedSearchTerm;
    $[1] = searchIndex;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const matchedIndexes = t1;
  const matchedCount = matchedIndexes.length;
  let t2;
  if ($[3] !== matchedCount) {
    t2 = () => {
      setVisibleCount(prev => Math.min(prev + INITIAL_VISIBLE_PROJECTS, matchedCount));
    };
    $[3] = matchedCount;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const loadMore = t2;
  const [scrollProgress, isLoading] = useLazyScroll(loadMore, matchedCount, visibleCount);
  let t3;
  let t4;
  if ($[5] !== matchedCount || $[6] !== matchedIndexes || $[7] !== visibleCount) {
    t3 = () => {
      const cards = getProjectCards();
      const visibleIndexes = new Set(matchedIndexes.slice(0, visibleCount));
      cards.forEach((card, index_0) => {
        card.hidden = !visibleIndexes.has(index_0);
      });
      const emptyState = document.getElementById(PROJECTS_EMPTY_STATE_ID);
      if (emptyState) {
        emptyState.hidden = matchedCount !== 0;
      }
    };
    t4 = [matchedCount, matchedIndexes, visibleCount];
    $[5] = matchedCount;
    $[6] = matchedIndexes;
    $[7] = visibleCount;
    $[8] = t3;
    $[9] = t4;
  } else {
    t3 = $[8];
    t4 = $[9];
  }
  useEffect(t3, t4);
  const shouldShowScrollLabel = scrollProgress > 0 && visibleCount < matchedCount;
  const canLoadMore = visibleCount < matchedCount;
  let t5;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = event => {
      setSearchTerm(event.target.value);
      setVisibleCount(INITIAL_VISIBLE_PROJECTS);
    };
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  const onSearchTermChange = t5;
  let t6;
  if ($[11] !== searchTerm) {
    t6 = <div className="w-full max-w-3xl"><SearchInput searchTerm={searchTerm} onSearchTermChange={onSearchTermChange} /></div>;
    $[11] = searchTerm;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  const t7 = shouldShowScrollLabel ? "opacity-100" : "hidden";
  let t8;
  if ($[13] !== t7) {
    t8 = cn("col-span-full flex items-center justify-center transition-opacity duration-300", t7);
    $[13] = t7;
    $[14] = t8;
  } else {
    t8 = $[14];
  }
  const t9 = !canLoadMore;
  let t10;
  if ($[15] !== loadMore || $[16] !== t9) {
    t10 = <button type="button" className="border-border-color text-content-text shadow-custom hover:shadow-custom-low inline-flex items-center justify-center rounded-full border bg-(--bg) px-6 py-2 text-sm font-semibold transition-transform duration-200 hover:-translate-y-0.5 disabled:pointer-events-none disabled:opacity-0" onClick={loadMore} disabled={t9}>Load more projects</button>;
    $[15] = loadMore;
    $[16] = t9;
    $[17] = t10;
  } else {
    t10 = $[17];
  }
  const t11 = canLoadMore ? "opacity-100" : "opacity-0";
  let t12;
  if ($[18] !== t11) {
    t12 = cn("ml-2 text-sm text-gray-500 dark:text-gray-400", t11);
    $[18] = t11;
    $[19] = t12;
  } else {
    t12 = $[19];
  }
  const t13 = isLoading ? "Loading more\u2026" : "Scroll to load more";
  let t14;
  if ($[20] !== t12 || $[21] !== t13) {
    t14 = <div aria-live="polite" className={t12}>{t13}</div>;
    $[20] = t12;
    $[21] = t13;
    $[22] = t14;
  } else {
    t14 = $[22];
  }
  const t15 = canLoadMore ? "opacity-100" : "opacity-0";
  let t16;
  if ($[23] !== t15) {
    t16 = cn("animate-ping", t15);
    $[23] = t15;
    $[24] = t16;
  } else {
    t16 = $[24];
  }
  let t17;
  if ($[25] === Symbol.for("react.memo_cache_sentinel")) {
    t17 = <MdArrowDownward className="text-gray-500 dark:text-gray-400" />;
    $[25] = t17;
  } else {
    t17 = $[25];
  }
  let t18;
  if ($[26] !== t16) {
    t18 = <div className={t16}>{t17}</div>;
    $[26] = t16;
    $[27] = t18;
  } else {
    t18 = $[27];
  }
  let t19;
  if ($[28] !== t10 || $[29] !== t14 || $[30] !== t18) {
    t19 = <div className="mb-12 flex flex-col items-center justify-center gap-4">{t10}{t14}{t18}</div>;
    $[28] = t10;
    $[29] = t14;
    $[30] = t18;
    $[31] = t19;
  } else {
    t19 = $[31];
  }
  let t20;
  if ($[32] !== t19 || $[33] !== t8) {
    t20 = <div className={t8}>{t19}</div>;
    $[32] = t19;
    $[33] = t8;
    $[34] = t20;
  } else {
    t20 = $[34];
  }
  let t21;
  if ($[35] !== t20 || $[36] !== t6) {
    t21 = <>{t6}{t20}</>;
    $[35] = t20;
    $[36] = t6;
    $[37] = t21;
  } else {
    t21 = $[37];
  }
  return t21;
}